import { NgClass, NgStyle } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import {
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    NavigationStart,
    Router,
    RouterOutlet
} from '@angular/router';
import { SidebarComponent } from '@common/components/sidebar/sidebar.component';
import { Setting } from '@common/interfaces/setting.interface';
import { SettingKeyCodes } from '@common/known-types/setting-key.codes';
import { User } from '@common/models/user';
import { CommonService } from '@common/services/common.service';
import { WebsocketService } from '@common/services/websocket.service';
import { tapSuccessResult } from '@ngneat/query';
import { LoadingBarModule, LoadingBarService } from '@ngx-loading-bar/core';
import { TranslatePipe } from '@ngx-translate/core';
import { DialogContainerDirective } from '@progress/kendo-angular-dialog';
import { SplitterComponent, SplitterPaneComponent } from '@progress/kendo-angular-layout';
import { DarkModeService } from '@services/dark-mode.service';
import { I18nService } from '@services/i18n.service';
import { SettingsService } from '@services/settings.service';
import { UserService } from '@services/user.service';
import _ from 'lodash';
import { NgxSpinnerComponent } from 'ngx-spinner';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { NavItem } from './navigation';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        LoadingBarModule,
        NgxSpinnerComponent,
        DialogContainerDirective,
        NgClass,
        TranslatePipe,
        SidebarComponent,
        SplitterComponent,
        SplitterPaneComponent,
        NgStyle,
        RouterOutlet
    ]
})
export class AppComponent implements OnInit, OnDestroy {
    commonService = inject(CommonService);
    @ViewChild('consoleSplitter', { static: false }) consoleSplitter: SplitterComponent;
    isAuthenticated = false;
    backgroundImageUrl: SafeUrl;
    loginBackgroundImageUrl: SafeUrl;
    currentUser: User;
    destroy$ = new Subject<boolean>();
    private websocketService = inject(WebsocketService);
    private router = inject(Router);
    private loadingBarService = inject(LoadingBarService);
    private darkModeService = inject(DarkModeService);
    private userService = inject(UserService);
    private i18nService = inject(I18nService);
    private settingsService = inject(SettingsService);

    ngOnInit() {
        // Execute right after Angular’s initial setup but before any next macrotask
        queueMicrotask(async () => await this.userService.isJwtAuthenticated());

        this.userService.isAuthenticatedSubject.pipe(distinctUntilChanged()).subscribe((value) => {
            this.isAuthenticated = value;
            if (this.isAuthenticated) this.getSettings();
            else this.getLoginSettings();
        });

        this.userService.currentUserSubject.subscribe((value) => {
            this.currentUser = value;
            if (this.currentUser)
                try {
                    this.websocketService.info('Client connected');
                    this.i18nService.setLanguageFromUser(this.currentUser);
                } catch {
                    /* empty */
                }
        });

        const routerSate = this.loadingBarService.useRef('router');
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                routerSate.start();
            } else if (event instanceof NavigationError) {
                routerSate.complete();
                console.error(event.error);
            } else if (event instanceof NavigationEnd) {
                routerSate.complete();
            } else if (event instanceof NavigationCancel) {
                routerSate.complete();
            }
        });
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    hasPermission(item: NavItem) {
        if (!this.currentUser) return false;

        const permissions = _.isArray(item.permissions) ? item.permissions : item.permissions ? [item.permissions] : [];
        if (!item.permissions || (_.isArray(item.permissions) && item.permissions.length === 0)) {
            return true;
        }

        return this.currentUser.hasPermission(...permissions);
    }

    private handleImageUpload(fileContent: string) {
        if (!fileContent) return null;
        const decodedData = atob(fileContent);
        const uint8Array = new Uint8Array(decodedData.length);
        for (let i = 0; i < decodedData.length; ++i) {
            uint8Array[i] = decodedData.charCodeAt(i);
        }
        const blob = new Blob([uint8Array], { type: 'image/jpeg' });
        return URL.createObjectURL(blob);
    }

    private getSettings() {
        this.settingsService
            .getSettings()
            .pipe(
                takeUntil(this.destroy$),
                tapSuccessResult((result: any) => {
                    const appBackgroundImageContent = result.find(
                        (setting: Setting) => setting.key === SettingKeyCodes.AppBackgroundImage
                    )?.value;
                    this.backgroundImageUrl = this.handleImageUpload(appBackgroundImageContent);
                })
            )
            .subscribe();
    }

    private getLoginSettings() {
        this.settingsService
            .getLoginSettings()
            .pipe(
                takeUntil(this.destroy$),
                tapSuccessResult((result: Setting[]) => {
                    const loginBackgroundImageContent = result.find(
                        (setting: Setting) => setting.key === SettingKeyCodes.LoginBackgroundImage
                    )?.value;
                    this.loginBackgroundImageUrl = this.handleImageUpload(loginBackgroundImageContent);
                })
            )
            .subscribe();
    }
}
